// Draws the bench as SVG from the engine's View. Static scenery is drawn
// once; the scene layer is rebuilt when the bench changes; live values
// (displays, LED glow, lamps) are updated every frame.

import { BATTERY, BOARD, BREADBOARD, DMM, Geometry, SUPPLY, WORK_SCALE, knobAngle, type KnobName, type Point } from "./geometry";
import type { Band, Endpoint, Layout, PlacedPart, View } from "./types";

const NS = "http://www.w3.org/2000/svg";

export const BAND_COLORS: Record<Band, string> = {
  black: "#111", brown: "#6b3a12", red: "#d22", orange: "#f28a1a", yellow: "#f2d21a",
  green: "#2a9d3a", blue: "#2457d6", violet: "#8b3fc8", grey: "#8a8a8a", white: "#f4f4f4",
  gold: "#d4a62a", silver: "#c0c0c8",
};

export const LED_COLORS: Record<string, string> = {
  red: "#e53935", green: "#43a047", yellow: "#fdd835", blue: "#1e88e5", white: "#eeeeee",
};

export const WIRE_COLORS: Record<string, string> = {
  red: "#d32f2f", black: "#222", blue: "#1565c0", green: "#2e7d32", yellow: "#f9a825",
  white: "#eceff1", orange: "#ef6c00",
};

export function el<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string | number> = {}, text?: string): SVGElementTagNameMap[K] {
  const e = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  if (text !== undefined) e.textContent = text;
  return e;
}

export interface Layers {
  svg: SVGSVGElement;
  scenery: SVGGElement;
  scene: SVGGElement;
  live: SVGGElement;
  overlay: SVGGElement;
}

export function makeLayers(svg: SVGSVGElement): Layers {
  const scenery = el("g", { id: "scenery" });
  const scene = el("g", { id: "scene" });
  const live = el("g", { id: "live" });
  const overlay = el("g", { id: "overlay" });
  svg.append(scenery, scene, live, overlay);
  return { svg, scenery, scene, live, overlay };
}

// ---------------------------------------------------------------------------
// Scenery: supply, battery, DMM body, breadboard, board.

export function drawScenery(g: SVGGElement, layout: Layout, geo: Geometry): void {
  g.replaceChildren();
  // Bench supply.
  const s = el("g", { class: "supply" });
  s.append(el("rect", { x: SUPPLY.x, y: SUPPLY.y, width: SUPPLY.w, height: SUPPLY.h, rx: 2, class: "panel" }));
  s.append(el("text", { x: SUPPLY.x + 4, y: SUPPLY.y + 7, class: "label" }, "Bench power supply"));
  const lcd = SUPPLY.lcd;
  s.append(el("rect", { id: "psu-lcd", x: lcd.x, y: lcd.y, width: lcd.w, height: lcd.h, rx: 1, class: "lcd standby" }));
  s.append(el("text", { id: "psu-v", x: lcd.x + lcd.w / 2, y: lcd.y + 10, class: "lcd-text", "text-anchor": "middle" }, "5.00 V"));
  s.append(el("text", { id: "psu-i", x: lcd.x + lcd.w / 2, y: lcd.y + 19, class: "lcd-text small", "text-anchor": "middle" }, "0.100 A"));
  s.append(el("text", { id: "psu-mode", x: lcd.x + lcd.w - 2, y: lcd.y + 4.5, class: "tiny lcd-tag", "text-anchor": "end" }, "SET"));
  s.append(el("circle", { id: "psu-cv", cx: SUPPLY.x + 82, cy: SUPPLY.y + 18, r: 2, class: "lamp" }));
  s.append(el("text", { x: SUPPLY.x + 86, y: SUPPLY.y + 19.5, class: "tiny" }, "CV"));
  s.append(el("circle", { id: "psu-cc", cx: SUPPLY.x + 82, cy: SUPPLY.y + 26, r: 2, class: "lamp" }));
  s.append(el("text", { x: SUPPLY.x + 86, y: SUPPLY.y + 27.5, class: "tiny" }, "CC"));
  // Knobs.
  for (const [name, k] of Object.entries(SUPPLY.knobs)) {
    const g = el("g", { class: "knob", "data-knob": name, tabindex: 0, role: "slider", "aria-label": `${k.label.toLowerCase()} setting`, "aria-valuemin": 0, "aria-valuemax": k.max, "aria-valuenow": 0 });
    g.append(el("title", {}, `${k.label}: drag up or down, scroll, or use the arrow keys`));
    for (const deg of [-135, 135]) {
      const a = (deg - 90) * Math.PI / 180;
      g.append(el("line", { x1: k.cx + (k.r + 0.8) * Math.cos(a), y1: k.cy + (k.r + 0.8) * Math.sin(a), x2: k.cx + (k.r + 2) * Math.cos(a), y2: k.cy + (k.r + 2) * Math.sin(a), class: "knob-tick" }));
    }
    g.append(el("circle", { cx: k.cx, cy: k.cy, r: k.r, class: "knob-body" }));
    g.append(el("line", { id: `psu-knob-${name}`, x1: k.cx, y1: k.cy - 1.5, x2: k.cx, y2: k.cy - k.r + 1, class: "knob-pointer" }));
    g.append(el("text", { x: k.cx, y: k.cy + k.r + 5, class: "tiny", "text-anchor": "middle" }, k.label));
    s.append(g);
  }
  // Output button.
  const o = SUPPLY.output;
  const btn = el("g", { id: "psu-on", class: "button", role: "button", tabindex: 0, "aria-label": "Output on or off", "aria-pressed": "false" });
  btn.append(el("title", {}, "Output: press to switch the supply output on or off"));
  btn.append(el("rect", { x: o.x, y: o.y, width: o.w, height: o.h, rx: 1.2, class: "button-body" }));
  btn.append(el("circle", { id: "psu-on-lamp", cx: o.x + o.w / 2, cy: o.y + 3.5, r: 1.4, class: "lamp" }));
  btn.append(el("text", { x: o.x + o.w / 2, y: o.y + 9.5, class: "tiny", "text-anchor": "middle" }, "OUTPUT"));
  s.append(btn);
  s.append(jack(SUPPLY.jacks.pos, "red", "psu", "pos", "+"));
  s.append(jack(SUPPLY.jacks.neg, "black", "psu", "neg", "−"));
  g.append(s);

  // Battery.
  const b = el("g", { class: "battery" });
  b.append(el("rect", { x: BATTERY.x, y: BATTERY.y, width: BATTERY.w, height: BATTERY.h, rx: 2, class: "battery-body" }));
  b.append(el("text", { x: BATTERY.x + BATTERY.w / 2, y: BATTERY.y + 30, class: "label", "text-anchor": "middle" }, "9 V"));
  b.append(el("text", { x: BATTERY.x + BATTERY.w / 2, y: BATTERY.y + 38, class: "tiny", "text-anchor": "middle" }, "battery"));
  b.append(jack(BATTERY.jacks.pos, "red", "bat", "pos", "+"));
  b.append(jack(BATTERY.jacks.neg, "black", "bat", "neg", "−"));
  g.append(b);

  // DMM body.
  const d = el("g", { class: "dmm" });
  d.append(el("rect", { x: DMM.x, y: DMM.y, width: DMM.w, height: DMM.h, rx: 3, class: "panel dmm-body" }));
  d.append(el("text", { x: DMM.x + 4, y: DMM.y + 5, class: "label" }, "Digital multimeter"));
  d.append(el("rect", { x: DMM.display.x, y: DMM.display.y, width: DMM.display.w, height: DMM.display.h, rx: 1, class: "lcd" }));
  d.append(el("text", { id: "dmm-text", x: DMM.display.x + DMM.display.w - 3, y: DMM.display.y + 12.5, class: "lcd-text big", "text-anchor": "end" }, ""));
  d.append(el("text", { id: "dmm-beep", x: DMM.display.x + 2, y: DMM.display.y + 4.5, class: "tiny" }, ""));
  d.append(el("text", { id: "dmm-fuse", x: DMM.display.x + 2, y: DMM.display.y + DMM.display.h + 4.5, class: "tiny warn" }, ""));
  // Dial.
  d.append(el("circle", { cx: DMM.dial.cx, cy: DMM.dial.cy, r: DMM.dial.r, class: "dial" }));
  d.append(el("line", { id: "dmm-pointer", x1: DMM.dial.cx, y1: DMM.dial.cy, x2: DMM.dial.cx, y2: DMM.dial.cy - DMM.dial.r + 2, class: "dial-pointer" }));
  const modes: [string, string, number][] = [["off", "OFF", -120], ["vdc", "V⎓", -60], ["adc", "A⎓", 0], ["ohms", "Ω", 60], ["continuity", "•)))", 120]];
  const modeNames: Record<string, string> = { off: "off", vdc: "DC volts", adc: "DC amps", ohms: "ohms", continuity: "continuity" };
  for (const [mode, label, deg] of modes) {
    const a = (deg - 90) * Math.PI / 180;
    // A dot at each position is the click target; the label beyond it is too.
    const rd = DMM.dial.r + 1.8, rl = DMM.dial.r + 5.5;
    const dot = el("g", { class: "dial-stop", "data-mode": mode, tabindex: 0, role: "button", "aria-label": `Meter ${modeNames[mode]}` });
    dot.append(el("circle", { cx: DMM.dial.cx + rd * Math.cos(a), cy: DMM.dial.cy + rd * Math.sin(a), r: 2.2, class: "dial-dot-hit" }));
    dot.append(el("circle", { cx: DMM.dial.cx + rd * Math.cos(a), cy: DMM.dial.cy + rd * Math.sin(a), r: 0.9, class: "dial-dot" }));
    dot.append(el("title", {}, `Turn the dial to ${modeNames[mode]}`));
    d.append(dot);
    d.append(el("text", { x: DMM.dial.cx + rl * Math.cos(a), y: DMM.dial.cy + rl * Math.sin(a) + 1, class: "tiny dial-label", "text-anchor": "middle", "data-mode": mode }, label));
  }
  // Jacks.
  d.append(jack(DMM.jacks.COM, "black", "dmm", "COM", "COM"));
  d.append(jack(DMM.jacks.V, "red", "dmm", "V", "VΩ"));
  d.append(jack(DMM.jacks.mA, "red", "dmm", "mA", "mA"));
  d.append(jack(DMM.jacks["10A"], "red", "dmm", "10A", "10A"));
  g.append(d);

  // Breadboard.
  const bb = el("g", { class: "breadboard" });
  const S = WORK_SCALE;
  const bbW = layout.breadboard.widthMm * S, bbH = layout.breadboard.heightMm * S;
  bb.append(el("rect", { x: BREADBOARD.x, y: BREADBOARD.y, width: bbW, height: bbH, rx: 1.5 * S, class: "bb-body" }));
  const railLine = (y: number, cls: string) => el("line", { x1: BREADBOARD.x + 3 * S, y1: y, x2: BREADBOARD.x + bbW - 3 * S, y2: y, class: cls });
  for (const h of layout.breadboard.holes) {
    if (/^t\+2$/.test(h.id) || /^t-2$/.test(h.id) || /^b\+2$/.test(h.id) || /^b-2$/.test(h.id)) {
      const plus = h.id[1] === "+";
      // The stripe runs along the outside of each rail pair: above the top
      // pair's − row and below its + row; above the bottom pair's + row and
      // below its − row.
      const top = h.id[0] === "t";
      const outside = (top === plus ? 1.6 : -1.6);
      bb.append(railLine(BREADBOARD.y + (h.y + outside) * S, plus ? "rail-plus" : "rail-minus"));
      bb.append(el("text", { x: BREADBOARD.x + 1.4 * S, y: BREADBOARD.y + h.y * S + 1, class: "tiny rail-label" }, plus ? "+" : "−"));
      bb.append(el("text", { x: BREADBOARD.x + bbW - 2.8 * S, y: BREADBOARD.y + h.y * S + 1, class: "tiny rail-label" }, plus ? "+" : "−"));
    }
  }
  const gapY = (geo.hole("e1")!.y + geo.hole("f1")!.y) / 2;
  bb.append(el("line", { x1: BREADBOARD.x + 1, y1: gapY, x2: BREADBOARD.x + bbW - 1, y2: gapY, class: "bb-gap" }));
  for (const h of layout.breadboard.holes) {
    const p = geo.hole(h.id)!;
    const c = el("circle", { cx: p.x, cy: p.y, r: 0.55 * S, class: "hole", "data-hole": h.id });
    c.append(el("title", {}, h.id));
    bb.append(c);
  }
  for (let col = 1; col <= 30; col++) {
    if (col === 1 || col % 5 === 0) {
      const p = geo.hole(`a${col}`)!;
      bb.append(el("text", { x: p.x, y: p.y - 2 * S, class: "tiny bb-label", "text-anchor": "middle" }, String(col)));
      const q = geo.hole(`j${col}`)!;
      bb.append(el("text", { x: q.x, y: q.y + 2.3 * S, class: "tiny bb-label", "text-anchor": "middle" }, String(col)));
    }
  }
  for (const row of "abcdefghij") {
    const p = geo.hole(`${row}1`)!;
    bb.append(el("text", { x: p.x - 2 * S, y: p.y + 0.7, class: "tiny bb-label", "text-anchor": "middle" }, row));
  }
  g.append(bb);

  // Board.
  const board = el("g", { class: "board" });
  const bW = layout.board.sizeMm[0] * S, bH = layout.board.sizeMm[1] * S;
  board.append(el("rect", { x: BOARD.x, y: BOARD.y, width: bW, height: bH, rx: 1.5 * S, class: "board-body" }));
  board.append(el("text", { x: BOARD.x + bW / 2, y: BOARD.y + bH * 0.45, class: "label", "text-anchor": "middle" }, layout.board.name));
  board.append(el("text", { id: "board-state", x: BOARD.x + bW / 2, y: BOARD.y + bH * 0.45 + 7, class: "tiny", "text-anchor": "middle" }, "unpowered"));
  board.append(el("circle", { id: "board-led", cx: BOARD.x + bW - 9, cy: BOARD.y + bH * 0.45 + 12, r: 1.2 * S, class: "lamp" }));
  board.append(el("text", { x: BOARD.x + bW - 9, y: BOARD.y + bH * 0.45 + 17, class: "tiny", "text-anchor": "middle" }, "ON"));
  for (const pin of layout.board.pins) {
    const p = geo.terminal("board", pin.name)!;
    const isSupply = layout.board.supplyPins.includes(pin.name) || layout.board.groundPins.includes(pin.name);
    const sq = el("rect", { x: p.x - 0.9 * S, y: p.y - 0.9 * S, width: 1.8 * S, height: 1.8 * S, class: `pin ${isSupply ? "pin-power" : ""}`, "data-instrument": "board", "data-terminal": pin.name });
    sq.append(el("title", {}, pin.name));
    board.append(sq);
    const top = pin.y < layout.board.sizeMm[1] / 2;
    const ly = top ? p.y + 4.2 * S : p.y - 2.2 * S;
    const t = el("text", { x: p.x, y: ly, class: "pin-label", "text-anchor": "end", transform: `rotate(-90 ${p.x} ${ly})` }, pin.name.replace(/^GND\d$/, "GND"));
    if (top) t.setAttribute("text-anchor", "end");
    else t.setAttribute("text-anchor", "start");
    board.append(t);
  }
  g.append(board);
}

function jack(p: Point, color: string, instrument: string, terminal: string, label: string): SVGGElement {
  const g = el("g", { class: "jack", "data-instrument": instrument, "data-terminal": terminal });
  g.append(el("circle", { cx: p.x, cy: p.y, r: 2.6, class: `jack-ring jack-${color}` }));
  g.append(el("circle", { cx: p.x, cy: p.y, r: 1.1, class: "jack-hole" }));
  g.append(el("text", { x: p.x, y: p.y + 5.6, class: "tiny", "text-anchor": "middle" }, label));
  g.append(el("title", {}, `${label} (${color})`));
  return g;
}

// ---------------------------------------------------------------------------
// Scene: parts, leads, probes.

export interface ProbePositions { red: Point; black: Point }

/**
 * Triggered view of a trace: the window starts 20% before the last rising
 * crossing of the mean, so a periodic signal stays still on the screen. Without
 * a crossing the window ends at `now`.
 */
export function triggerView(trace: [number, number][], timePerDiv: number, now: number): { t0: number; span: number; pts: [number, number][] } {
  const span = timePerDiv * 10;
  if (trace.length < 2) return { t0: now - span, span, pts: [] };
  const first = trace[0][0], last = trace[trace.length - 1][0];
  const mean = trace.reduce((a, p) => a + p[1], 0) / trace.length;
  // The last rising crossing whose whole window (20% before, 80% after) is on the trace.
  let t0: number | null = null;
  for (let i = 1; i < trace.length; i++) {
    const a = trace[i - 1][1] - mean, b = trace[i][1] - mean;
    if (a < 0 && b >= 0) {
      const c = trace[i - 1][0] + (-a / (b - a)) * (trace[i][0] - trace[i - 1][0]);
      if (c - 0.2 * span >= first && c + 0.8 * span <= last) t0 = c - 0.2 * span;
    }
  }
  if (t0 === null) t0 = last - span;
  return { t0, span, pts: trace.filter(([t]) => t >= t0 && t <= t0 + span) };
}

/** The oscilloscope drawn in the DMM's place, for scope activities. */
function drawScope(g: SVGGElement, view: View, geo: Geometry): void {
  const { x, y, w, h } = DMM;
  const sc = view.state.scope;
  const d = el("g", { class: "scope" });
  d.append(el("rect", { x, y, width: w, height: h, rx: 3, class: "panel scope-body" }));
  d.append(el("text", { x: x + 4, y: y + 5, class: "label" }, "Oscilloscope"));
  const sx = x + 4, sy = y + 9, sw = w - 8, sh = h - 24;
  d.append(el("rect", { x: sx, y: sy, width: sw, height: sh, rx: 1, class: "lcd" }));
  const cols = 10, rows = 8;
  for (let i = 1; i < cols; i++) d.append(el("line", { x1: sx + (i * sw) / cols, y1: sy, x2: sx + (i * sw) / cols, y2: sy + sh, class: "scope-grid" }));
  for (let j = 1; j < rows; j++) d.append(el("line", { x1: sx, y1: sy + (j * sh) / rows, x2: sx + sw, y2: sy + (j * sh) / rows, class: "scope-grid" }));
  d.append(el("line", { x1: sx, y1: sy + sh / 2, x2: sx + sw, y2: sy + sh / 2, class: "scope-grid scope-axis" }));
  const span = sc.timePerDiv * cols;
  if (sc.on && view.trace.length > 1) {
    const tv = triggerView(view.trace, sc.timePerDiv, view.time);
    const pts = tv.pts.map(([t, volt]) => {
      const px = sx + ((t - tv.t0) / tv.span) * sw;
      const py = sy + sh / 2 - (volt / sc.voltsPerDiv) * (sh / rows);
      return `${px.toFixed(2)},${Math.min(sy + sh, Math.max(sy, py)).toFixed(2)}`;
    });
    d.append(el("polyline", { points: pts.join(" "), class: "scope-trace" }));
  } else {
    d.append(el("text", { x: sx + sw / 2, y: sy + sh / 2 + 1.5, class: "tiny", "text-anchor": "middle" }, sc.on ? "waiting for the trace" : "scope off"));
  }
  const ts = sc.timePerDiv >= 1e-3 ? `${+(sc.timePerDiv * 1e3).toPrecision(3)} ms` : `${+(sc.timePerDiv * 1e6).toPrecision(3)} µs`;
  d.append(el("text", { x: x + 4, y: y + h - 3, class: "tiny" }, `${ts}/div · ${sc.voltsPerDiv} V/div`));
  // Probe tips, where the probes touch.
  for (const [lead, at, colour] of [["red", sc.red, "#c62828"], ["black", sc.black, "#222"]] as const) {
    const p = at ? geo.endpoint(at) : null;
    if (p) d.append(el("circle", { cx: p.x, cy: p.y, r: 1.4, class: `scope-probe ${lead}`, fill: colour }));
  }
  g.append(d);
}

export function drawScene(g: SVGGElement, view: View, geo: Geometry, probes: ProbePositions, selected: string | null, scope = false): void {
  g.replaceChildren();
  if (scope) drawScope(g, view, geo);
  for (const lead of view.state.leads) {
    const a = geo.endpoint(lead.from), b = geo.endpoint(lead.to);
    if (!a || !b) continue;
    const w = el("g", { class: `lead ${selected === lead.id ? "selected" : ""}`, "data-lead": lead.id, tabindex: 0, role: "button", "aria-label": `${lead.color || "wire"} wire ${lead.id}` });
    w.append(el("path", { d: wirePath(a, b), class: "lead-outline" }));
    w.append(el("path", { d: wirePath(a, b), class: "lead-core", stroke: WIRE_COLORS[lead.color] ?? "#888" }));
    w.append(el("title", {}, `${lead.color || "wire"} wire ${lead.id}`));
    g.append(w);
  }
  for (const part of view.state.parts) {
    const node = drawPart(part, view, geo, selected === part.id);
    if (node) g.append(node);
  }
  // Probes: lead from jack to tip.
  const redJack = DMM.jacks[view.state.meter.redJack];
  g.append(probe("red", redJack, probes.red, view.state.meter.red));
  g.append(probe("black", DMM.jacks.COM, probes.black, view.state.meter.black));
}

// A probe is a handle leaning up and to the right with a thin needle down to
// the tip, so the hole it sits in stays visible. The invisible circle at the
// tip is the drag handle and focus target.
function probe(color: "red" | "black", from: Point, tip: Point, at: Endpoint | null): SVGGElement {
  const g = el("g", { class: `probe probe-${color}`, "data-probe": color });
  const stroke = color === "red" ? "#d32f2f" : "#222";
  const needle = { x: tip.x + 1.1, y: tip.y - 2.2 };
  const grip = { x: tip.x + 4.6, y: tip.y - 9.2 };
  g.append(el("path", { d: wirePath(from, grip, 1.5), class: "probe-lead", stroke }));
  g.append(el("line", { x1: grip.x, y1: grip.y, x2: needle.x, y2: needle.y, class: "probe-body", stroke }));
  g.append(el("line", { x1: needle.x, y1: needle.y, x2: tip.x, y2: tip.y, class: "probe-needle" }));
  g.append(el("circle", { cx: tip.x, cy: tip.y, r: 2.4, class: "probe-tip", "data-probe": color, tabindex: 0, role: "button", "aria-label": `${color} probe${at ? ", on " + (at.kind === "hole" ? at.hole : at.terminal) : ", lifted"}` }));
  g.append(el("text", { x: grip.x + 1.6, y: grip.y + 1, class: "tiny" }, color === "red" ? "red" : "blk"));
  g.append(el("title", {}, `${color} probe`));
  return g;
}

/** A part's label: small text on a rounded pill so it reads on any background. */
function partLabel(x: number, y: number, text: string): SVGGElement {
  const g = el("g", { class: "part-label" });
  const w = text.length * 1.3 + 2.4, h = 3.4;
  g.append(el("rect", { x: x - w / 2, y: y - 2.5, width: w, height: h, rx: h / 2, class: "label-pill" }));
  g.append(el("text", { x, y, class: "tiny label-text", "text-anchor": "middle" }, text));
  return g;
}

function wirePath(a: Point, b: Point, sag = 0.35): string {
  const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len;
  // Short jumpers stay nearly straight; long runs bow like real wire.
  const bulge = Math.min(10, len * sag * (len / (len + 30)));
  return `M${a.x},${a.y} Q${mx + nx * bulge},${my + ny * bulge} ${b.x},${b.y}`;
}

function drawPart(part: PlacedPart, view: View, geo: Geometry, selected: boolean): SVGGElement | null {
  const pts = part.holes.map((h) => geo.hole(h));
  if (pts.some((p) => !p)) return null;
  const p = pts as Point[];
  const info = view.parts[part.id];
  const result = view.solution.components[part.id];
  const dead = info?.health === "open";
  const g = el("g", {
    class: `part part-${part.spec.kind} ${selected ? "selected" : ""} ${dead ? "dead" : ""}`,
    "data-part": part.id, tabindex: 0, role: "button",
    "aria-label": `${info?.label ?? part.spec.kind} ${part.id}${dead ? ", failed" : ""}`,
  });
  const S = WORK_SCALE; // part bodies scale with the breadboard pitch
  const lift = 2.0 * S; // body sits just above its holes, clear of the neighbouring rows
  switch (part.spec.kind) {
    case "capacitor": {
      const [a, b] = p;
      const y = Math.min(a.y, b.y) - lift;
      const mx = (a.x + b.x) / 2, gap = 0.9 * S, plate = 3.6 * S;
      g.append(el("line", { x1: a.x, y1: a.y, x2: a.x, y2: y, class: "leg" }));
      g.append(el("line", { x1: b.x, y1: b.y, x2: b.x, y2: y, class: "leg" }));
      g.append(el("line", { x1: a.x, y1: y, x2: mx - gap, y2: y, class: "leg" }));
      g.append(el("line", { x1: mx + gap, y1: y, x2: b.x, y2: y, class: "leg" }));
      g.append(el("line", { x1: mx - gap, y1: y - plate / 2, x2: mx - gap, y2: y + plate / 2, class: "leg" }));
      g.append(el("line", { x1: mx + gap, y1: y - plate / 2, x2: mx + gap, y2: y + plate / 2, class: "leg" }));
      g.append(partLabel(mx, y - 2.4 * S, part.id));
      break;
    }
    case "inductor": {
      const [a, b] = p;
      const y = Math.min(a.y, b.y) - lift;
      const left = Math.min(a.x, b.x), width = Math.abs(b.x - a.x);
      const coil = Math.max(6 * S, width - 3.5 * S), x0 = left + (width - coil) / 2;
      g.append(el("line", { x1: a.x, y1: a.y, x2: a.x, y2: y, class: "leg" }));
      g.append(el("line", { x1: b.x, y1: b.y, x2: b.x, y2: y, class: "leg" }));
      g.append(el("line", { x1: a.x, y1: y, x2: x0, y2: y, class: "leg" }));
      g.append(el("line", { x1: x0 + coil, y1: y, x2: b.x, y2: y, class: "leg" }));
      // Four loops of coil, each a half circle above the wire.
      const loops = 4, r = coil / (2 * loops);
      let d = `M${x0},${y}`;
      for (let k = 0; k < loops; k++) d += ` a${r},${r} 0 0 1 ${2 * r},0`;
      g.append(el("path", { d, class: "leg", fill: "none" }));
      g.append(partLabel(x0 + coil / 2, y - 2.4 * S, part.id));
      break;
    }
    case "functionGenerator": {
      const [a, b] = p;
      const y = Math.min(a.y, b.y) - lift;
      const left = Math.min(a.x, b.x), width = Math.abs(b.x - a.x);
      const bw = Math.max(6 * S, width - 2 * S), bx = left + (width - bw) / 2;
      g.append(el("line", { x1: a.x, y1: a.y, x2: a.x, y2: y, class: "leg" }));
      g.append(el("line", { x1: b.x, y1: b.y, x2: b.x, y2: y, class: "leg" }));
      g.append(el("rect", { x: bx, y: y - 2.4 * S, width: bw, height: 4.8 * S, rx: 1 * S, class: "resistor-body", fill: "#eef3e8" }));
      const shape = part.spec.kind === "functionGenerator" ? part.spec.waveform : "sine";
      const glyph = shape === "square" ? "⊓" : shape === "triangle" ? "△" : "∿";
      g.append(el("text", { x: bx + bw / 2, y: y + 1.6 * S, "font-size": 4.8, "text-anchor": "middle" }, glyph));
      g.append(partLabel(bx + bw / 2, y - 3.2 * S, part.id));
      break;
    }
    case "resistor": {
      const [a, b] = p;
      const y = Math.min(a.y, b.y) - lift;
      g.append(el("line", { x1: a.x, y1: a.y, x2: a.x, y2: y, class: "leg" }));
      g.append(el("line", { x1: b.x, y1: b.y, x2: b.x, y2: y, class: "leg" }));
      g.append(el("line", { x1: a.x, y1: y, x2: b.x, y2: y, class: "leg" }));
      const left = Math.min(a.x, b.x), width = Math.abs(b.x - a.x);
      const bw = Math.max(5.5 * S, width - 3.5 * S), bx = left + (width - bw) / 2;
      g.append(el("rect", { x: bx, y: y - 1.6 * S, width: bw, height: 3.2 * S, rx: 1.2 * S, class: "resistor-body" }));
      const bands = info?.bands ?? [];
      bands.forEach((band, i) => {
        const n = bands.length;
        const gap = i === n - 1 ? 1.0 : 0;
        const x = bx + 0.9 * S + i * ((bw - 2.6 * S) / n) + gap * S;
        g.append(el("rect", { x, y: y - 1.6 * S, width: 0.7 * S, height: 3.2 * S, fill: BAND_COLORS[band], class: "band" }));
      });
      if (dead) g.append(el("text", { x: bx + bw / 2, y: y + 1.1, class: "tiny dead-mark", "text-anchor": "middle" }, "✕"));
      // Only the id is printed: the value is read from the bands (the hover
      // key names the colours), which is the point of the exercise.
      g.append(partLabel(bx + bw / 2, y - 2.4 * S, part.id));
      break;
    }
    case "led": {
      const [a, k] = p;
      const cx = (a.x + k.x) / 2, cy = Math.min(a.y, k.y) - lift - 1.5 * S;
      const color = part.spec.color;
      const lit = result?.kind === "led" && result.lit && !dead;
      const brightness = result?.kind === "led" ? result.brightness : 0;
      g.append(el("circle", { id: `glow-${part.id}`, cx, cy, r: 5 * S, class: "led-glow", fill: LED_COLORS[color], opacity: lit ? brightness * 0.8 : 0 }));
      g.append(el("line", { x1: a.x, y1: a.y, x2: cx - 0.8 * S, y2: cy + 2 * S, class: "leg" }));
      g.append(el("line", { x1: k.x, y1: k.y, x2: cx + 0.8 * S, y2: cy + 2 * S, class: "leg" }));
      // Dome with straight sides down to the base at cy + 2S, where the legs end.
      g.append(el("path", { d: `M${cx - 2.5 * S},${cy + 2 * S} V${cy} a${2.5 * S},${2.5 * S} 0 0 1 ${5 * S},0 V${cy + 2 * S} Z`, class: "led-body", fill: dead ? "#444" : LED_COLORS[color], "fill-opacity": lit ? 1 : 0.55 }));
      g.append(el("line", { x1: cx - 2.9 * S, y1: cy + 2 * S, x2: cx + 2.9 * S, y2: cy + 2 * S, class: "led-base", stroke: dead ? "#444" : LED_COLORS[color], "stroke-width": 0.5 * S, "stroke-linecap": "round", "stroke-opacity": lit ? 1 : 0.7 }));
      g.append(el("text", { x: cx, y: cy + 0.9 * S, class: "tiny led-letter", "text-anchor": "middle" }, dead ? "✕" : color[0].toUpperCase()));
      g.append(partLabel(a.x, Math.min(a.y, k.y) + 2.2 * S, "+"));
      g.append(partLabel(cx, cy - 4 * S, `${part.id} ${color}`));
      break;
    }
    case "switch": {
      const [a, b] = p;
      const y = Math.min(a.y, b.y) - lift;
      g.append(el("line", { x1: a.x, y1: a.y, x2: a.x, y2: y, class: "leg" }));
      g.append(el("line", { x1: b.x, y1: b.y, x2: b.x, y2: y, class: "leg" }));
      g.append(el("circle", { cx: a.x, cy: y, r: 0.6 * S, class: "contact" }));
      g.append(el("circle", { cx: b.x, cy: y, r: 0.6 * S, class: "contact" }));
      const ex = part.closed ? b.x - 0.6 * S : b.x - 1.2 * S, ey = part.closed ? y : y - 2.6 * S;
      g.append(el("line", { x1: a.x, y1: y, x2: ex, y2: ey, class: "lever" }));
      g.append(partLabel((a.x + b.x) / 2, y - 3.2 * S, `${part.id} ${part.closed ? "closed" : "open"}`));
      break;
    }
    case "potentiometer": {
      const [a, w, b] = p;
      const y = Math.min(a.y, w.y, b.y) - lift;
      for (const q of [a, w, b]) g.append(el("line", { x1: q.x, y1: q.y, x2: q.x, y2: y, class: "leg" }));
      const cx = w.x, cy = y - 3 * S;
      g.append(el("rect", { x: cx - 4 * S, y: cy - 3 * S, width: 8 * S, height: 6.5 * S, rx: 0.8 * S, class: "pot-body" }));
      g.append(el("circle", { cx, cy, r: 2.2 * S, class: "pot-knob" }));
      const ang = (-135 + 270 * part.position) * Math.PI / 180;
      g.append(el("line", { x1: cx, y1: cy, x2: cx + 2 * S * Math.sin(ang), y2: cy - 2 * S * Math.cos(ang), class: "pot-pointer" }));
      g.append(partLabel(cx, cy - 4.2 * S, `${part.id} ${info?.label ?? ""} ${Math.round(part.position * 100)}%`));
      break;
    }
  }
  return g;
}

// ---------------------------------------------------------------------------
// Live values.

export function updateLive(view: View): void {
  const set = (id: string, text: string) => { const n = document.getElementById(id); if (n && n.textContent !== text) n.textContent = text; };
  const lamp = (id: string, on: boolean) => document.getElementById(id)?.classList.toggle("on", on);
  const s = view.supply;
  const st = view.state.supply;
  // Output off: the display shows the settings, like a real supply in standby.
  const live = !!s && st.on;
  set("psu-v", live ? s.voltageText : `${st.setVoltage.toFixed(2)} V`);
  set("psu-i", live ? s.currentText : `${st.currentLimit.toFixed(3)} A`);
  set("psu-mode", st.on ? "" : "SET");
  document.getElementById("psu-lcd")?.classList.toggle("standby", !st.on);
  lamp("psu-cv", !!s && s.mode === "cv");
  lamp("psu-cc", !!s && s.mode === "cc");
  lamp("psu-on-lamp", st.on);
  const button = document.getElementById("psu-on");
  button?.classList.toggle("on", st.on);
  button?.setAttribute("aria-pressed", String(st.on));
  for (const name of Object.keys(SUPPLY.knobs) as KnobName[]) {
    const k = SUPPLY.knobs[name];
    const value = st[k.field];
    document.getElementById(`psu-knob-${name}`)?.setAttribute("transform", `rotate(${knobAngle(name, value)} ${k.cx} ${k.cy})`);
    const g = document.querySelector<SVGElement>(`[data-knob="${name}"]`);
    if (g && g.getAttribute("aria-valuenow") !== String(value)) {
      g.setAttribute("aria-valuenow", String(value));
      g.setAttribute("aria-valuetext", name === "volt" ? `${value.toFixed(1)} volts` : `${value.toFixed(2)} amps`);
    }
  }
  const m = view.meter;
  set("dmm-text", view.state.meter.mode === "off" ? "" : (m?.text ?? ""));
  set("dmm-beep", m?.beep ? "♪ beep" : "");
  set("dmm-fuse", view.fuseBlown ? "FUSE BLOWN" : "");
  const pointer = document.getElementById("dmm-pointer");
  if (pointer) {
    const deg = { off: -120, vdc: -60, adc: 0, ohms: 60, continuity: 120 }[view.state.meter.mode] ?? -120;
    pointer.setAttribute("transform", `rotate(${deg} ${DMM.dial.cx} ${DMM.dial.cy})`);
  }
  for (const n of document.querySelectorAll<SVGElement>(".dial-label, .dial-stop")) n.classList.toggle("on", n.dataset.mode === view.state.meter.mode);
  for (const n of document.querySelectorAll<SVGElement>(".dmm .jack")) {
    const t = n.dataset.terminal;
    n.classList.toggle("in-use", t === "COM" || t === view.state.meter.redJack);
  }
  const board = view.solution.components["board"];
  const powered = view.state.board.powered;
  lamp("board-led", powered);
  set("board-state", powered ? `powered by ${view.state.board.powerInput === "usb" ? "USB" : "barrel jack"}` : "unpowered");
  if (board?.kind === "board") {
    // In certificate mode the server sends only `overloaded`, not the pins.
    const pub = board as unknown as { overloaded?: boolean };
    const over = pub.overloaded ?? Object.values(board.pins ?? {}).some((p) => p.overcurrent);
    if (over) set("board-state", "pin overloaded");
  }
  for (const part of view.state.parts) {
    if (part.spec.kind !== "led") continue;
    const r = view.solution.components[part.id];
    const g = document.getElementById(`glow-${part.id}`);
    if (g && r?.kind === "led") g.setAttribute("opacity", String(r.lit && view.parts[part.id]?.health === "ok" ? r.brightness * 0.8 : 0));
  }
}
